import {Component, computed, effect, inject, input, OnInit, output, signal, untracked, WritableSignal} from '@angular/core';
import {PrimeNGConfig} from 'primeng/api';
import {AlertService, DateService, DateUtils, isNotNullOrUndefined} from 'sigale-angular';
import {PcalendarLanguageService} from '@sigale-angular/src/services/pcalendar-language.service';
import {AuthService} from '../../services/auth-service.service';
import {EvenementService} from '../../services/evenement.service';
import {UtilisateurSigalett} from '../../models/utilisateur-sigalett.model';
import {Semaine} from './model/semaine.model';
import {finalize} from 'rxjs/operators';
import {EvenementDto} from '../../dto/evenement.dto';
import {DateUtilsService} from '../../services/date-utils.service';
import {forkJoin} from 'rxjs';
import {ReservationService} from '../../services/reservation.service';
import {ReservationDto} from '../../dto/reservation.dto';
import {TranslateService} from '@ngx-translate/core';
import {PlageSelectionnee} from '../../models/plage-selectionnee.model';
import {EvenementCalendrier} from './model/evenement-calendrier.model';
import {Evenement} from './model/evenement.model';
import {Reservation} from './model/reservation.model';
import {Duree} from '../../models/duree.model';
import {ZoneDeTravailService} from '../../services/zone-de-travail.service';
import {OccupationZoneDeTravailDto} from '../../dto/occupation-zone-de-travail.dto';
import {Router} from '@angular/router';
import {BureauPreferentielOccupationDto} from '../../dto/bureau-preferentiel-occupation.dto';

@Component({
	selector: 'app-calendrier',
	templateUrl: './calendrier.component.html',
	styleUrl: './calendrier.component.scss'
})
export class CalendrierComponent implements OnInit {
	readonly NOM_ALERTE_ERREUR_GENERALE = 'visualisationEvenements';

	masquerReservations = input.required<boolean>();
	afficherWeekend = input.required<boolean>();

	plagesSelectionneesChange = output<PlageSelectionnee[]>();

	semaines: WritableSignal<Semaine[]> = signal([]);
	semaines7jours: WritableSignal<Semaine[]> = signal([]);
	evenementParIdParDateEvenement: WritableSignal<Map<string, Map<number, EvenementDto>>> = signal(new Map<string, Map<number, EvenementDto>>());
	nombreSemaines: WritableSignal<number> = signal(5); // La plupart des mois ont seulement besoin de 5 semaines d'affichage
	loadingCalendrier: WritableSignal<boolean> = signal(false);
	plagesSelectionnees = signal<PlageSelectionnee[]>([]);
	premiereDateCalendrier: WritableSignal<Date> = signal(new Date());
	derniereDateCalendrier: WritableSignal<Date> = signal(new Date());
	evenementsCalendrierParDate = signal(new Map<string, EvenementCalendrier[]>());

	// signaux pour la gestion de la modale de détail
	evenementSelectionne = signal<Evenement>(null);
	reservationSelectionne = signal<Reservation>(null);
	showDetailEvenementModal = signal<boolean>(false);
	showDetailReservationModal = signal<boolean>(false);

	loadingTauxOccupation = signal<boolean>(false);
	loadingJourneeBureauPreferentiel = signal<boolean>(false);

	nombreJoursAffichesParSemaine = computed(() => this.afficherWeekend() ? 7:5);
	estSemaine7jours = computed(() => this.nombreJoursAffichesParSemaine()==7);
	estSemaine5jours = computed(() => this.nombreJoursAffichesParSemaine()==5);

	journeesAvecBureauPreferentiel = signal<BureauPreferentielOccupationDto[]>([]);

	showPopupInformationConflit = signal(false);
	dateAnnulationEvenement = signal<Date | null>(null);

	dateActuelle = signal<Date>(new Date());

	labelJoursComplet: string[] = [];
	labelJoursAbreviation: string[] = [];
	utilisateurConnecte: UtilisateurSigalett;
	occupationZoneDeTravailParJour: Map<string, OccupationZoneDeTravailDto> = new Map<string, OccupationZoneDeTravailDto>();

	private router = inject(Router);

	constructor(
			private authService: AuthService,
			private primengConfig: PrimeNGConfig,
			private dateService: DateService,
			private pcalendarLanguageService: PcalendarLanguageService,
			private evenementService: EvenementService,
			private reservationService: ReservationService,
			private alertService: AlertService,
			private dateUtilsService: DateUtilsService,
			private translateService: TranslateService,
			private zoneDeTravailService: ZoneDeTravailService) {

		effect(() => {
			this.afficherWeekend();

			untracked(() => {
				this.reinitGlobal();
			});
		});
	}

	ngOnInit(): void {
		this.utilisateurConnecte = this.authService.getUtilisateurConnecte();
		this.dateActuelle.set(new Date());
	}

	private reinitGlobal(): void {
		this.initLabelAvecTraduction();
		this.modifierLesSemainesDuCalendrierAvecDateActuelle();
		this.rechercherEvenementsEtReservationsPourMoisCourant();
		this.recupererOccupationsZoneParDefautUtilisateur();
		this.recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant();
	}

	/**
	 * Actualise les évènements et vide les jours sélectionnés en notifiant le parent.
	 */
	rechargerCalendrier(): void {
		this.viderDatesSelectionnees();
		this.rechercherEvenementsEtReservationsPourMoisCourant();
		this.recupererOccupationsZoneParDefautUtilisateur();
		this.recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant();
	}

	/**
	 * Recherche les évènements et les réservations pour le mois courant.
	 */
	rechercherEvenementsEtReservationsPourMoisCourant(): void {
		this.loadingCalendrier.set(true);
		const login = this.utilisateurConnecte.login;
		const dateDebut = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(this.premiereDateCalendrier(), this.utilisateurConnecte.langue);
		const dateFin = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(this.derniereDateCalendrier(), this.utilisateurConnecte.langue);
		forkJoin([
			this.evenementService.recupererEvenementsPourLoginDemandeurEtPeriode([login], dateDebut, dateFin),
			this.reservationService.recupererReservationPourPeriodeEtOccupant(dateDebut, dateFin, login)])
				.pipe(finalize(() => this.loadingCalendrier.set(false)))
				.subscribe({
					next: ([evenements, reservations]) => {
						this.construireEvenementCalendrier(evenements, reservations);
					},
					error: (error) => {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					},
				});
	}

	/**
	 * Récupère le taux d'occupations de la zone par défaut de l'utilisateur
	 */
	recupererOccupationsZoneParDefautUtilisateur(): void {
		this.loadingTauxOccupation.set(true);

		const dateDebut = this.premiereDateCalendrier();
		const dateFin = this.dateUtilsService.recupererDimancheDansDeuxSemaines();
		if (dateFin < dateDebut) {
			this.loadingTauxOccupation.set(false);
			return;
		}

		this.zoneDeTravailService.recupererTauxOccupationZoneDeTravailPourPeriode(dateDebut, dateFin, this.utilisateurConnecte.login)
				.pipe(finalize(() => this.loadingTauxOccupation.set(false)))
				.subscribe({
					next: occupationsZoneDeTravailDtos => {
						occupationsZoneDeTravailDtos.forEach(occupationZoneDeTravail => {
							const cleDate = this.dateUtilsService.formaterDateEnCle(new Date(occupationZoneDeTravail.date));
							this.occupationZoneDeTravailParJour.set(cleDate, occupationZoneDeTravail);
						});
					},
					error: (error) => {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					}
				});
	}

	/**
	 * Recupère les journées où l'utilisateur a un bureau préférentiel et n'a pas besoin de réserver.
	 */
	recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant(): void {
		this.loadingJourneeBureauPreferentiel.set(true);

		const dateDebut = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(new Date(), this.utilisateurConnecte.langue);
		const dateFin = this.dateUtilsService.formaterDateEnStringPourEnvoiBack(this.dateUtilsService.recupererDimancheDansDeuxSemaines(), this.utilisateurConnecte.langue);

		this.reservationService.recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant(dateDebut, dateFin, this.utilisateurConnecte.login)
				.pipe(finalize(() => this.loadingJourneeBureauPreferentiel.set(false)))
				.subscribe({
					next: journeesAvecBureauPreferentiel => {
						const bureauxPreferentielOccupations = journeesAvecBureauPreferentiel.map(journee => {
							return new BureauPreferentielOccupationDto(
									new Date(journee.journeeOccupee),
									journee.estReserveParAutreOccupant
							);
						});
						this.journeesAvecBureauPreferentiel.set(bureauxPreferentielOccupations);

						if (isNotNullOrUndefined(this.dateAnnulationEvenement())) {
							const bureauAvecBureauPreferentiel = this.recupererBureauPreferentielOccupationSelonJournee(this.dateAnnulationEvenement());
							if (bureauAvecBureauPreferentiel.estReserveParAutreOccupant) {
								this.showPopupInformationConflit.set(true);
							}
						}
					},
					error: (error) => {
						this.alertService.clear(this.NOM_ALERTE_ERREUR_GENERALE);
						this.alertService.error(error.error, this.NOM_ALERTE_ERREUR_GENERALE);
					}
				});
	}

	onFermerPopupInformationConflit(event: boolean): void {
		this.showPopupInformationConflit.set(event);
		if (event===false) {
			this.dateAnnulationEvenement.set(null);
		}
	}

	/**
	 * Retourne True si le jour courant passé en paramètre est un jour avec le bureau préférentiel.
	 *
	 * @param jourCourant le jour courant.
	 */
	estJourneeAvecBureauPreferentiel(jourCourant: Date): boolean {
		return this.recupererBureauPreferentielOccupationSelonJournee(jourCourant)!=undefined;
	}

	onClickVoirEtatZoneDeTravail(jourCourant: Date): void {
		this.router.navigate(['/visionner-taux-occupation'], {
			queryParams: {
				date: this.dateUtilsService.formaterDateEnStringPourEnvoiBack(jourCourant, this.utilisateurConnecte.langue)
			}
		});
	}

	/**
	 * Construit les évènements du calendrier.
	 * @param evenementsDtos les évènements (absences, télétravail)
	 * @param reservationsDtos les réservations.
	 */
	construireEvenementCalendrier(evenementsDtos: EvenementDto[], reservationsDtos: ReservationDto[]): void {
		const evenementsCalendrierParDate = new Map<string, EvenementCalendrier[]>();

		this.semaines7jours().flatMap(semaine => semaine.jours).forEach(jour => {
			const cle = this.dateUtilsService.formaterDateEnCle(jour);
			evenementsCalendrierParDate.set(cle, []);
		});

		const evenementsCalendriers: EvenementCalendrier[] = [];

		evenementsDtos.forEach(evenementDto => {
			const evenement = new Evenement(
					evenementDto.id,
					new Date(evenementDto.dateEvenement),
					evenementDto.duree,
					evenementDto.dateDemande,
					evenementDto.typeEvenement,
					evenementDto.statut,
					evenementDto.commentaire,
					evenementDto.dateValidation,
					evenementDto.validateur,
					evenementDto.demandeur
			);
			evenementsCalendriers.push(evenement);
		});

		reservationsDtos.forEach(reservationDto => {
			const reservation = new Reservation(
					reservationDto.id,
					new Date(reservationDto.dateReservation),
					reservationDto.duree,
					reservationDto.bureauId,
					reservationDto.nomBureau,
					reservationDto.ilotId,
					reservationDto.nomIlot,
					reservationDto.couleurIlot
			);
			evenementsCalendriers.push(reservation);
		});

		const ordreDuree: Record<Duree, number> = {
			[Duree.JOURNEE]: 1,
			[Duree.MATIN]: 2,
			[Duree.APRES_MIDI]: 3,
		};

		evenementsCalendriers.sort((a: EvenementCalendrier, b: EvenementCalendrier) => ordreDuree[a.duree.code] - ordreDuree[b.duree.code]);

		evenementsCalendriers.forEach(evenementCalendrier => {
			const cle = this.dateUtilsService.formaterDateEnCle(evenementCalendrier.date);
			evenementsCalendrierParDate.get(cle).push(evenementCalendrier);
		});

		this.evenementsCalendrierParDate.set(evenementsCalendrierParDate);
	}

	/**
	 * Traduis dans la langue de l'utilisateur les jours et les mois.
	 */
	initLabelAvecTraduction(): void {
		const langueUtilisateur = this.utilisateurConnecte.langue;
		const labelsTraduis = this.pcalendarLanguageService.traduire(langueUtilisateur);

		// Je déplace le dimanche à la fin de la liste.
		const joursComplet = [...labelsTraduis.dayNames.slice(1), labelsTraduis.dayNames[0]];
		const joursAbreviation = [...labelsTraduis.dayNamesMin.slice(1), labelsTraduis.dayNamesMin[0]];

		this.labelJoursComplet = [];
		for (const jour of joursComplet.slice(0, this.nombreJoursAffichesParSemaine())) {
			this.labelJoursComplet.push(jour);
		}

		this.labelJoursAbreviation = [];
		for (const jour of joursAbreviation.slice(0, this.nombreJoursAffichesParSemaine())) {
			this.labelJoursAbreviation.push(jour);
		}

		this.primengConfig.setTranslation(labelsTraduis);
	}

	/**
	 * Met à jour la liste de semaines en fonction de la date actuelle.
	 */
	modifierLesSemainesDuCalendrierAvecDateActuelle(): void {
		const annee = this.dateActuelle().getFullYear();
		const mois = this.dateActuelle().getMonth();
		const jourDebutSemaine = 1;

		// mois : 0 (janvier) à 11 (décembre)
		const premierJourDuMois = new Date(annee, mois, 1);
		const dernierJourDuMois = new Date(annee, mois + 1, 0);

		// Jour de la semaine du premier du mois (0=dimanche, 1=lundi, ...)
		let premierJourSemaine = premierJourDuMois.getDay();
		// Ajustement pour semaine commençant lundi (premierJourSemaine = 1)
		premierJourSemaine = (premierJourSemaine===0) ? 7:premierJourSemaine;

		// Premier jour affiché dans le calendrier
		const dateCourante = new Date(premierJourDuMois);
		dateCourante.setDate(dateCourante.getDate() - (premierJourSemaine - jourDebutSemaine));

		const semaines: Semaine[] = [];
		const semaines7jours: Semaine[] = []; // ne sert qu'à calculer la dernière date du calendrier sur un calendrier de 7 jours
		do {
			const semaine: Date[] = [];
			const semaine7jours: Date[] = [];
			for (let i = 0; i < 7; i++) {
				if (this.estSemaine7jours() || (this.estSemaine5jours() && i < 5)) {
					semaine.push(new Date(dateCourante));
				}
				semaine7jours.push(new Date(dateCourante));
				dateCourante.setDate(dateCourante.getDate() + 1);
			}
			semaines.push(new Semaine(semaine));
			semaines7jours.push(new Semaine(semaine7jours));
		}
		while (
				// On continue tant qu'on n'a pas couvert tout le mois
		dateCourante <= dernierJourDuMois || (dateCourante.getDay()!==jourDebutSemaine && dateCourante.getDay()!==0));

		this.premiereDateCalendrier.set(semaines[0].jours[0]);
		this.derniereDateCalendrier.set(semaines7jours[semaines7jours.length - 1].jours[6]);

		this.nombreSemaines.set(semaines.length);
		this.semaines.set(semaines);
		this.semaines7jours.set(semaines7jours);
	}

	/**
	 * Détermine la classe pour le numéro du jour selon si c'est le jour courant ou un jour d'un autre mois.
	 * @param jour le jour concerné.
	 */
	determinerClasseNumeroJour(jour: Date): string {
		if (this.dateService.sontDatesMemeJour(jour, new Date())) {
			return 'jour-courant';
		}

		return jour.getMonth()!==this.dateActuelle().getMonth() ? 'jour-autres-mois':'';
	}

	/**
	 * Change le mois dans le calendrier et récupère les évènements/réservations.
	 */
	changerDeMois(nouvelleDate: Date): void {
		this.dateActuelle.set(nouvelleDate);
		this.modifierLesSemainesDuCalendrierAvecDateActuelle();
		this.rechercherEvenementsEtReservationsPourMoisCourant();
	}

	/**
	 * Ajoute/supprime un jour dans les jours sélectionnés.
	 * @param plageSelectionnee la plage sélectionnnée.
	 */
	selectionnerDeselectionnerPlageDisponible(plageSelectionnee: PlageSelectionnee): void {
		if (plageSelectionnee.estSelectionne) {
			this.plagesSelectionnees.update(plages => [...plages, plageSelectionnee]);
		} else {
			this.plagesSelectionnees.update(plages => plages.filter(plage =>
					!this.dateService.sontDatesMemeJour(plage.dateSelectionnee, plageSelectionnee.dateSelectionnee) || plage.duree!==plageSelectionnee.duree));
		}
		this.plagesSelectionneesChange.emit(this.plagesSelectionnees());
	}

	/**
	 * Vide la liste de date sélectionnée et notifie le parent.
	 */
	private viderDatesSelectionnees() {
		this.plagesSelectionnees.set([]);
		this.plagesSelectionneesChange.emit([]);
	}

	/**
	 * Ouvre la modale pour voir le détail d'un évènement.
	 *
	 * @param evenement l'évènement concerné.
	 */
	ouvrirDetailEvenementModal(evenement: Evenement): void {
		this.evenementSelectionne.set(evenement);
		this.showDetailEvenementModal.set(true);
	}

	/**
	 * Ouvre la modale pour voir le détail d'une réservation.
	 *
	 * @param reservation la réservation concernée.
	 */
	ouvrirDetailReservationModal(reservation: Reservation): void {
		this.reservationSelectionne.set(reservation);
		this.showDetailReservationModal.set(true);
	}

	/**
	 *  Met à jour la map en supprimant le jour annulé.
	 * @param evenementCalendrierAAnnuler l'évènement calendrier annulé.
	 */
	onAnnulerEvenementCalendrier(evenementCalendrierAAnnuler: EvenementCalendrier): void {
		const cle = this.dateUtilsService.formaterDateEnCle(evenementCalendrierAAnnuler.date);

		this.evenementsCalendrierParDate.update(evenementsCalendrierParDate => {
			const nouvelleMap = new Map(evenementsCalendrierParDate);

			const evenementsCalendriersFiltres = nouvelleMap.get(cle).filter(evenementCalendrier => evenementCalendrier.uuid!==evenementCalendrierAAnnuler.uuid);

			nouvelleMap.set(cle, evenementsCalendriersFiltres);
			return nouvelleMap;
		});

		this.evenementSelectionne.set(null);
		this.reservationSelectionne.set(null);

		this.dateAnnulationEvenement.set(evenementCalendrierAAnnuler.date);

		this.recupererOccupationsZoneParDefautUtilisateur();
		this.recupererJourneeAvecPlacePreferentiellePourPeriodeEtOccupant();
	}

	estPlageSelectionnee(jour: Date): boolean {
		return this.plagesSelectionnees().some(plage => DateUtils.isSameDay(plage.dateSelectionnee, jour));
	}

	/**
	 * Vérifie si la journée passée en paramètre possède un taux d'occupation.
	 * @param journee la journée.
	 */
	estJourneePossedeTauxOccupation(journee: Date): boolean {
		const cleDate = this.dateUtilsService.formaterDateEnCle(journee);
		return this.occupationZoneDeTravailParJour.has(cleDate);
	}

	/**
	 * Renvoie le taux d'occupation de la journée.
	 * @param journee la journée.
	 */
	recupererTauxOccupationPourJournee(journee: Date): OccupationZoneDeTravailDto {
		const cleDate = this.dateUtilsService.formaterDateEnCle(journee);
		return this.occupationZoneDeTravailParJour.get(cleDate);
	}

	private recupererBureauPreferentielOccupationSelonJournee(journee: Date): BureauPreferentielOccupationDto | undefined {
		return this.journeesAvecBureauPreferentiel().find(bureauPreferentielOccupation =>
				this.dateService.sontDatesMemeJour(bureauPreferentielOccupation.journeeOccupee, journee));
	}

	iconeBureauPreferentielSelonJour(jourCourant: Date): string {
		const bureauPreferentielOccupation = this.recupererBureauPreferentielOccupationSelonJournee(jourCourant);
		if (isNotNullOrUndefined(bureauPreferentielOccupation) && bureauPreferentielOccupation.estReserveParAutreOccupant) {
			return 'pi pi-exclamation-triangle conflit-bureau-preferentiel';
		}

		return 'pi pi-bookmark-fill';
	}

	tooltipIconeBureauPreferentiel(jourCourant: Date): string {
		const bureauPreferentielOccupation = this.recupererBureauPreferentielOccupationSelonJournee(jourCourant);
		if (isNotNullOrUndefined(bureauPreferentielOccupation) && bureauPreferentielOccupation.estReserveParAutreOccupant) {
			return this.translateService.instant('ACCUEIL.TOOLTIP_BUREAU_PREFERENTIEL_CONFLIT');
		}

		return this.translateService.instant('ACCUEIL.TOOLTIP_BUREAU_PREFERENTIEL');
	}

	transformerDateJourEnCle(date: Date): string {
		return this.dateUtilsService.formaterDateEnCle(date);
	}

	protected readonly isNotNullOrUndefined = isNotNullOrUndefined;
}
